import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback } from 'react';

import { ApiError } from '@/shared/api/client';
import type { SessionPayload } from '@/shared/types/domain';
import type { PendingIdentity, PendingMfaState } from '@/shared/types/mfa';
import {
  chooseOrganisation as chooseOrganisationRequest,
  fetchSession,
  login as loginRequest,
  logout as logoutRequest,
  type LoginInput,
  type OrganisationChoiceInput,
  type SessionResponse,
  type SignInResponse,
} from './api';
import { pendingIdentityOf, pendingMfaState, toPendingPayload } from './mfa/session';
import { isSignInStep } from './organisationChoice';

export const SESSION_QUERY_KEY = ['session'] as const;

export interface SessionState {
  /** A COMPLETE session only — null while the MFA step is pending. */
  session: SessionPayload | null;
  isLoading: boolean;
  isAuthenticated: boolean;
  /**
   * The MFA step this session still owes (`challenge` or `enrol`), or null.
   *
   * A pending session is signed in as far as the password goes and nothing
   * further: it is not `isAuthenticated`, it has no `session`, and the shell
   * never renders for it.
   */
  mfaPending: PendingMfaState | null;
  /** Whose second step it is — name, address, language; nothing privileged. */
  pendingIdentity: PendingIdentity | null;
  error: ApiError | null;
}

/**
 * The signed-in session.
 *
 * Server state lives in TanStack Query rather than a global store, so there is
 * exactly one copy of the truth and it invalidates the way every other server
 * resource does.
 */
export function useSession(): SessionState {
  const query = useQuery({
    queryKey: SESSION_QUERY_KEY,
    queryFn: fetchSession,
    // A 401 means "not signed in", which is a valid answer rather than a
    // failure worth retrying.
    retry: (failureCount, error) =>
      !(error instanceof ApiError && error.isUnauthenticated) && failureCount < 2,
    staleTime: 60_000,
  });

  const error = query.error instanceof ApiError ? query.error : null;
  // A sign-in STEP is never a session, wherever it came from (defence in depth:
  // the sign-in hooks below never cache one).
  const data: SessionResponse | null =
    query.data === undefined || isSignInStep(query.data) ? null : query.data;
  const mfaPending = pendingMfaState(data);
  const session = data !== null && mfaPending === null ? (data as SessionPayload) : null;

  return {
    session,
    isLoading: query.isPending,
    isAuthenticated: session !== null,
    mfaPending,
    pendingIdentity: mfaPending === null ? null : pendingIdentityOf(data),
    error: error?.isUnauthenticated ? null : error,
  };
}

/**
 * Caches what a sign-in step answered — when it is a session.
 *
 * A step (the organisation choice) opens no session and is never cached: it
 * lives in the sign-in screen's own state, so nothing could render the shell
 * for it, and a reload starts again at step 1.
 */
function useCacheSignIn() {
  const queryClient = useQueryClient();

  return useCallback(
    (result: SignInResponse) => {
      if (isSignInStep(result)) return;

      const session = result as SessionResponse;
      const pending = pendingMfaState(session);

      // A password alone opens only the MFA step: keep nothing else it sent.
      queryClient.setQueryData<SessionResponse>(
        SESSION_QUERY_KEY,
        pending === null ? session : toPendingPayload(session, pending),
      );
    },
    [queryClient],
  );
}

export function useLogin() {
  const cache = useCacheSignIn();

  return useMutation({
    mutationFn: (input: LoginInput) => loginRequest(input),
    // The password is this mutation's variables: gone as soon as the screen is.
    gcTime: 0,
    onSuccess: cache,
  });
}

/** Step 2: the organisation chosen. Answers a session exactly as step 1 does. */
export function useChooseOrganisation() {
  const cache = useCacheSignIn();

  return useMutation({
    mutationFn: (input: OrganisationChoiceInput) => chooseOrganisationRequest(input),
    // The selection token is this mutation's variables: gone with the screen.
    gcTime: 0,
    onSuccess: cache,
  });
}

export function useLogout() {
  const queryClient = useQueryClient();

  const reset = useCallback(() => {
    // Everything cached was scoped to the previous identity; removing rather
    // than invalidating avoids briefly rendering another user's data.
    queryClient.removeQueries({ predicate: (query) => query.queryKey[0] !== SESSION_QUERY_KEY[0] });
    // And the session is asked again — never just dropped from the cache: the
    // gate's own observer is told, so the sign-in form replaces the shell at
    // once (w9-to-w6-1). If the sign-out itself failed and the server still
    // holds the session, the shell comes back from that fresh read, not from
    // anything cached before.
    void queryClient.resetQueries({ queryKey: SESSION_QUERY_KEY, exact: true });
  }, [queryClient]);

  return useMutation({
    mutationFn: logoutRequest,
    onSettled: reset,
  });
}

/**
 * FND-13 — the organisation's own currency.
 *
 * Every amount a user AUTHORS — an approval threshold, a delegation cap — is
 * denominated in something, and until this existed the answer was a literal
 * 'SAR' in two components. A tenant operating in AED could only express SAR
 * thresholds, which then matched nothing, and the refusal blamed the user's
 * authority rather than the currency.
 *
 * Returns null before the session loads, so a caller renders nothing rather
 * than guessing. Never falls back to a literal: a wrong currency here is worse
 * than an absent one, because it silently produces a rule that never fires.
 */
export function useTenantCurrency(): string | null {
  const { session } = useSession();

  return session?.tenant?.default_currency ?? null;
}
